/* eslint-disable max-statements */

import { hashKey, useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TSDListItem } from '@e-origin/contracts';
import { CustomsStateNotificationType, SortDirection, TSDCriteria, TSDStatus, TSDType } from '@e-origin/shared';

import { queryClient } from '@hooks/queryClient';
import { showErrorToast } from '@hooks/showErrorToast';
import { apiClient } from '@utils';

export const defaultAdminTSDCriteria: TSDCriteria = {
  filters: {
    type: TSDType.COMBINED,
    searchQuery: '',
    status: [],
    filterByNotifications: [],
  },
  pagination: {
    page: 1,
    size: 10,
    direction: 1,
    searchToken: '',
    currentToken: '',
  },
  sorting: {
    field: 'counter',
    direction: SortDirection.DESC,
  },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): TSDCriteria => {
  const filters: TSDCriteria['filters'] = {
    type: (searchParams.get('type') as TSDType) || TSDType.COMBINED,
    searchQuery: searchParams.get('searchQuery') || '',
    status: searchParams.getAll('status') as TSDStatus[],
    filterByNotifications: searchParams.getAll('filterByNotifications') as CustomsStateNotificationType[],
  };

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'counter';
  const sortDir = (searchParams.get('sortDir') || SortDirection.DESC) as SortDirection;

  return {
    filters,
    pagination: {
      page,
      size,
      direction: 1,
      searchToken: '',
      currentToken: '',
    },
    sorting: {
      field: sortField,
      direction: sortDir,
    },
  };
};

const criteriaToSearchParams = (criteria: TSDCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  searchParams.set('type', criteria.filters.type);

  criteria.filters.status?.forEach((status) => {
    searchParams.append('status', status);
  });

  criteria.filters.filterByNotifications?.forEach((notificationType) => {
    searchParams.append('filterByNotifications', notificationType);
  });

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialAdminTSDCriteria = (searchParams: URLSearchParams): TSDCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }

  return defaultAdminTSDCriteria;
};

export const useAdminTSDCriteriaUrlSync = (criteria?: TSDCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);

    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);

    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

const fetchAdminTSDs = async (criteria: TSDCriteria): Promise<{ list: TSDListItem[]; total: number }> => {
  try {
    const result = await apiClient.adminTsd.retrieveTsds.mutation({
      body: {
        criteria,
      },
    });

    if (result.status !== 200) {
      throw result.body;
    }

    return {
      list: (result.body.data?.list ?? []).map((tsd) => ({
        ...tsd,
        updatedAt: tsd.updatedAt ? new Date(tsd.updatedAt) : tsd.updatedAt,
      })) as TSDListItem[],
      total: result.body.data?.totalItems ?? 0,
    };
  } catch (error) {
    showErrorToast(error, 'Failed to fetch admin temporary storages');

    return {
      list: [],
      total: 0,
    };
  }
};

export const useAdminTSDs = (criteria: TSDCriteria = defaultAdminTSDCriteria) => {
  return useQuery({
    queryKey: ['admin-temporary-storages', hashKey([criteria])],
    queryFn: () => fetchAdminTSDs(criteria),
  });
};

export const prefetchAdminTSDs = (criteria: TSDCriteria = defaultAdminTSDCriteria) =>
  queryClient.prefetchQuery({
    queryKey: ['admin-temporary-storages', hashKey([criteria])],
    queryFn: () => fetchAdminTSDs(criteria),
  });
